import { useState, useEffect } from 'react';
import { FiSave, FiUpload, FiTrash2, FiImage, FiLayout, FiShoppingBag, FiCreditCard, FiGrid, FiUsers, FiCamera, FiCheck, FiPlus } from 'react-icons/fi';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { useSettings } from '../../context/SettingsContext';
import AdminSidebar from '../../components/AdminSidebar';
import '../../styles/Admin.css';
import '../../styles/AdminSettings.css';

const AdminSettings = () => {
    const { refreshSettings } = useSettings();
    const [settings, setSettings] = useState({
        logo: '', navbarLogo: '', sideAd: '', headerBackground: '',
        heroImages: [], gcashQRCode: '', gcashAccountName: '', gcashAccountNumber: '', footerSmallIcon: '',
        lipTintImage: '', perfumeImage: '', beautySoapImage: '',
        allBestSellersImage: '', ourStoryImage: '', productHeroMedia: '',
        premiumBannerMedia: '', teamImages: [], galleryImages: [],
        paymentLogos: [],
        brandName: '', showBrandName: true, brandNamePosition: 'right',
        brandNameColor: '#2D2D2D', brandNameFontSize: 'medium',
        brandNameFontWeight: 'bold', headerLogoSize: 60
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [files, setFiles] = useState({});
    const [removedFields, setRemovedFields] = useState([]);
    const [activeTab, setActiveTab] = useState('branding');

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await api.get('/settings');
                setSettings(prev => ({
                    ...prev,
                    ...data,
                    heroImages: data.heroImages || [],
                    teamImages: data.teamImages || [],
                    galleryImages: data.galleryImages || [],
                    paymentLogos: data.paymentLogos || [],
                }));
            } catch (error) {
                toast.error('Failed to load settings');
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleFileChange = (e, field, multiple = false) => {
        if (multiple) {
            const selectedFiles = Array.from(e.target.files);
            setFiles(prev => ({ ...prev, [field]: selectedFiles }));
            toast.info(`${selectedFiles.length} file(s) selected for ${field}`);
        } else {
            const file = e.target.files[0];
            if (file) {
                setFiles(prev => ({ ...prev, [field]: file }));
                const reader = new FileReader();
                reader.onloadend = () => setSettings(prev => ({ ...prev, [field]: reader.result }));
                reader.readAsDataURL(file);
            }
        }
    };

    const handleRemoveField = (field) => {
        setSettings(prev => ({ ...prev, [field]: '' }));
        setFiles(prev => { const next = { ...prev }; delete next[field]; return next; });
        setRemovedFields(prev => prev.includes(field) ? prev : [...prev, field]);
    };

    const handleRemoveArrayItem = (field, index) => {
        setSettings(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
    };

    const handleSave = async () => {
        setSaving(true);
        const formData = new FormData();

        // Append files
        Object.entries(files).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(file => formData.append(key, file));
            } else {
                formData.append(key, value);
            }
        });

        // Append text fields
        const textFields = ['brandName', 'showBrandName', 'brandNamePosition', 'brandNameColor', 'brandNameFontSize', 'brandNameFontWeight', 'headerLogoSize', 'gcashAccountName', 'gcashAccountNumber'];
        textFields.forEach(field => {
            if (settings[field] !== undefined) formData.append(field, settings[field]);
        });

        // Send 'remove' for deleted single image fields
        removedFields.forEach(field => {
            if (!files[field]) formData.append(field, 'remove');
        });

        // Send removed array fields (heroImages, teamImages, galleryImages)
        ['heroImages', 'teamImages', 'galleryImages'].forEach(field => {
            if (settings[field] && settings[field].length === 0) formData.append(field, 'remove');
        });

        // paymentLogos: always send existing URLs so backend knows what to keep
        if (settings.paymentLogos && settings.paymentLogos.length === 0) {
            formData.append('paymentLogos', 'remove');
        } else if (settings.paymentLogos && settings.paymentLogos.length > 0) {
            // Always send current saved URLs - backend will combine these with any new uploads
            settings.paymentLogos.forEach(url => formData.append('paymentLogos_existing', url));
        }

        try {
            const { data } = await api.put('/settings', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSettings({ ...data, heroImages: data.heroImages || [], teamImages: data.teamImages || [], galleryImages: data.galleryImages || [] });
            setFiles({});
            setRemovedFields([]);
            await refreshSettings();
            toast.success('Settings saved successfully! ✨');
        } catch (error) {
            console.error('Save error:', error.response?.data || error.message);
            toast.error(error.response?.data?.message || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    // ─── Sub-components ──────────────────────────────────────────────────────────

    // ─── Sub-components ──────────────────────────────────────────────────────────

    const isVideoUrl = (url) => url && /\.(mp4|mov|webm|avi)$/i.test(url.split('?')[0]);

    const ImageCard = ({ label, field, hint, icon: Icon = FiImage }) => {
        const [isDragOver, setIsDragOver] = useState(false);
        const hasMedia = Boolean(settings[field]);
        const isStaged = Boolean(files[field]);
        const isVideo = isVideoUrl(settings[field]);

        const handleDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(true);
        };

        const handleDragLeave = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
        };

        const handleDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                const file = e.dataTransfer.files[0];
                setFiles(prev => ({ ...prev, [field]: file }));
                const reader = new FileReader();
                reader.onloadend = () => setSettings(prev => ({ ...prev, [field]: reader.result }));
                reader.readAsDataURL(file);
                toast.success(`Selected "${file.name}" for ${label}`);
            }
        };

        return (
            <div className={`settings-image-card ${isDragOver ? 'drag-over' : ''}`}>
                <div className="sic-header">
                    <div className="sic-icon"><Icon size={16} /></div>
                    <div className="sic-header-text">
                        <div className="sic-label">{label}</div>
                        {hint && <div className="sic-hint">{hint}</div>}
                    </div>
                </div>

                <div
                    className={`sic-preview-box ${!hasMedia ? 'empty' : ''} ${isDragOver ? 'drag-active' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {hasMedia ? (
                        <div className="sic-media-container">
                            {isVideo ? (
                                <video
                                    src={settings[field]}
                                    className="sic-media-element video"
                                    muted
                                    preload="metadata"
                                    controls={false}
                                />
                            ) : (
                                <img
                                    src={settings[field]}
                                    alt={label}
                                    className="sic-media-element image"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            )}
                            <div className="sic-fallback-empty" style={{ display: 'none' }}>
                                <FiImage size={24} />
                                <span>Preview Unavailable</span>
                            </div>

                            {/* Status Badges */}
                            <div className="sic-badge-layer">
                                {isStaged && (
                                    <span className="sic-status-pill staged">
                                        <FiCheck size={11} /> Changed (Unsaved)
                                    </span>
                                )}
                                <span className="sic-type-pill">
                                    {isVideo ? 'VIDEO (MP4)' : 'IMAGE (CONTAINED)'}
                                </span>
                            </div>

                            {/* Floating Action Controls */}
                            <div className="sic-actions-overlay">
                                <label className="sic-action-btn replace" title="Replace with new file">
                                    <input
                                        type="file"
                                        accept="image/*,video/mp4,video/mov,video/webm"
                                        onChange={(e) => handleFileChange(e, field)}
                                        style={{ display: 'none' }}
                                    />
                                    <FiUpload size={13} />
                                    <span>Replace</span>
                                </label>

                                <button
                                    type="button"
                                    className="sic-action-btn remove"
                                    onClick={() => handleRemoveField(field)}
                                    title="Remove this media"
                                >
                                    <FiTrash2 size={13} />
                                    <span>Remove</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <label className="sic-dropzone">
                            <input
                                type="file"
                                accept="image/*,video/mp4,video/mov,video/webm"
                                onChange={(e) => handleFileChange(e, field)}
                                style={{ display: 'none' }}
                            />
                            <div className="sic-dropzone-icon">
                                <FiUpload size={24} />
                            </div>
                            <div className="sic-dropzone-main-text">
                                <strong>Click to upload</strong> or drag & drop
                            </div>
                            <div className="sic-dropzone-sub-text">
                                PNG, JPG, WEBM, MP4 • 100% Uncropped
                            </div>
                        </label>
                    )}
                </div>

                {isStaged && files[field]?.name && (
                    <div className="sic-file-meta">
                        <span className="sic-file-name" title={files[field].name}>
                            📁 {files[field].name}
                        </span>
                        <span className="sic-file-size">
                            {(files[field].size / 1024).toFixed(1)} KB
                        </span>
                    </div>
                )}
            </div>
        );
    };

    const ArrayImageSection = ({ label, field, maxCount, hint, icon: Icon = FiCamera }) => {
        const [isDragOver, setIsDragOver] = useState(false);
        const currentItems = settings[field] || [];
        const isStaged = Boolean(files[field]);

        const handleDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(true);
        };

        const handleDragLeave = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
        };

        const handleDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                const newFiles = Array.from(e.dataTransfer.files);
                setFiles(prev => ({ ...prev, [field]: newFiles }));
                toast.info(`${newFiles.length} file(s) staged for ${label}`);
            }
        };

        return (
            <div className="settings-array-section">
                <div className="sas-header">
                    <div className="sas-title-row">
                        <div className="sas-title"><Icon size={16} /> {label}</div>
                        <span className="sas-count-pill">
                            {currentItems.length} / {maxCount} Media Slots
                        </span>
                    </div>
                    {hint && <p className="sas-hint">{hint}</p>}
                </div>

                <div
                    className={`sas-grid-container ${isDragOver ? 'drag-over' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {currentItems.map((url, i) => (
                        <div key={i} className="sas-item-card">
                            <div className="sas-preview-wrapper">
                                {isVideoUrl(url) ? (
                                    <video
                                        src={url}
                                        className="sas-media-element"
                                        preload="metadata"
                                        muted
                                    />
                                ) : (
                                    <img
                                        src={url}
                                        alt={`${label} ${i + 1}`}
                                        className="sas-media-element"
                                    />
                                )}
                                <div className="sas-item-overlay">
                                    <button
                                        type="button"
                                        className="sas-item-remove-btn"
                                        onClick={() => handleRemoveArrayItem(field, i)}
                                        title="Delete this image"
                                    >
                                        <FiTrash2 size={13} />
                                    </button>
                                </div>
                            </div>
                            <div className="sas-item-footer">
                                <span className="sas-item-index">#{i + 1}</span>
                                {i === 0 && <span className="sas-primary-pill">Primary</span>}
                            </div>
                        </div>
                    ))}

                    {currentItems.length < maxCount && (
                        <label className={`sas-add-dropzone ${isDragOver ? 'active' : ''}`}>
                            <input
                                type="file"
                                accept="image/*,video/mp4,video/mov,video/webm"
                                multiple
                                onChange={(e) => handleFileChange(e, field, true)}
                                style={{ display: 'none' }}
                            />
                            <div className="sas-add-icon"><FiPlus size={20} /></div>
                            <span className="sas-add-main">Add {label}</span>
                            <span className="sas-add-sub">Drag & drop or browse</span>
                            {isStaged && (
                                <span className="sas-selected-badge">
                                    <FiCheck size={10} /> {files[field].length} selected
                                </span>
                            )}
                        </label>
                    )}
                </div>
            </div>
        );
    };

    // ─── Tab Content ─────────────────────────────────────────────────────────────

    const tabs = [
        { id: 'branding', label: 'Branding', icon: FiShoppingBag },
        { id: 'hero', label: 'Hero & Layout', icon: FiLayout },
        { id: 'categories', label: 'Categories', icon: FiGrid },
        { id: 'marketing', label: 'Marketing', icon: FiCamera },
        { id: 'payments', label: 'Payments', icon: FiCreditCard },
        { id: 'team', label: 'Team & Gallery', icon: FiUsers },
    ];

    if (loading) return (
        <div className="admin-layout">
            <AdminSidebar />
            <main className="admin-main"><div className="settings-loading">Loading settings...</div></main>
        </div>
    );

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <main className="admin-main settings-main">

                {/* Header */}
                <div className="settings-page-header">
                    <div>
                        <h1>Site Settings</h1>
                        <p>Customize your store's appearance and media</p>
                    </div>
                    <button className="settings-save-btn" onClick={handleSave} disabled={saving}>
                        <FiSave size={16} />
                        {saving ? 'Saving...' : 'Save All Changes'}
                    </button>
                </div>

                {/* Mobile Section Dropdown Selector */}
                <div className="settings-mobile-tab-bar">
                    <label htmlFor="settings-mobile-select" className="settings-mobile-tab-label">
                        Section:
                    </label>
                    <select
                        id="settings-mobile-select"
                        className="settings-mobile-tab-select"
                        value={activeTab}
                        onChange={(e) => setActiveTab(e.target.value)}
                    >
                        {tabs.map(tab => (
                            <option key={tab.id} value={tab.id}>
                                {tab.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Tab Navigation Pills */}
                <div className="settings-tabs">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                            type="button"
                        >
                            <tab.icon size={15} />
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="settings-content">

                    {/* ── BRANDING ─────────────────────────────── */}
                    {activeTab === 'branding' && (
                        <div className="settings-section">
                            <div className="settings-section-title">Branding</div>
                            <p className="settings-section-desc">Your store's logos and visual identity.</p>
                            <div className="settings-image-grid">
                                <ImageCard label="Navbar Logo" field="navbarLogo" hint="Logo shown in the navigation bar" icon={FiImage} />
                                <ImageCard label="Footer Icon" field="footerSmallIcon" hint="Small icon in the footer" icon={FiImage} />
                            </div>

                            <div className="settings-section-title" style={{ marginTop: '2rem' }}>Brand Name</div>
                            <div className="settings-form-row">
                                <div className="settings-form-group">
                                    <label>Brand Name</label>
                                    <input className="settings-input" value={settings.brandName || ''} onChange={e => setSettings(p => ({ ...p, brandName: e.target.value }))} placeholder="AmaraCé" />
                                </div>
                                <div className="settings-form-group">
                                    <label>Logo Size (px)</label>
                                    <input className="settings-input" type="number" value={settings.headerLogoSize || 60} onChange={e => setSettings(p => ({ ...p, headerLogoSize: Number(e.target.value) }))} min={30} max={150} />
                                </div>
                                <div className="settings-form-group">
                                    <label>Name Color</label>
                                    <div className="settings-color-row">
                                        <input className="settings-input" value={settings.brandNameColor || '#2D2D2D'} onChange={e => setSettings(p => ({ ...p, brandNameColor: e.target.value }))} />
                                        <input type="color" value={settings.brandNameColor || '#2D2D2D'} onChange={e => setSettings(p => ({ ...p, brandNameColor: e.target.value }))} className="settings-color-picker" />
                                    </div>
                                </div>
                                <div className="settings-form-group">
                                    <label>Show Brand Name</label>
                                    <label className="settings-toggle">
                                        <input type="checkbox" checked={settings.showBrandName !== false} onChange={e => setSettings(p => ({ ...p, showBrandName: e.target.checked }))} />
                                        <span className="settings-toggle-slider" />
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── HERO & LAYOUT ────────────────────────── */}
                    {activeTab === 'hero' && (
                        <div className="settings-section">
                            <div className="settings-section-title">Hero Slider</div>
                            <p className="settings-section-desc">Images/videos that appear in the homepage hero carousel (max 5).</p>
                            <ArrayImageSection label="Hero Slides" field="heroImages" maxCount={5} hint="Supports images and videos (MP4, MOV). Recommended: 1600×900px." icon={FiLayout} />

                            <div className="settings-section-title" style={{ marginTop: '2rem' }}>Layout Images</div>
                            <div className="settings-image-grid">
                                <ImageCard label="Product Hero Media" field="productHeroMedia" hint="Banner shown on the Products page" icon={FiImage} />
                                <ImageCard label="Premium Banner" field="premiumBannerMedia" hint="Right panel of the Beauty Essentials banner" icon={FiImage} />
                            </div>
                        </div>
                    )}

                    {/* ── CATEGORIES ───────────────────────────── */}
                    {activeTab === 'categories' && (
                        <div className="settings-section">
                            <div className="settings-section-title">Category Images</div>
                            <p className="settings-section-desc">Images shown in the "Shop by Category" section on the home page.</p>
                            <div className="settings-image-grid">
                                <ImageCard label="Lip Tint" field="lipTintImage" hint="Category thumbnail for Lip Tints" icon={FiGrid} />
                                <ImageCard label="Fragrances" field="perfumeImage" hint="Category thumbnail for Perfumes" icon={FiGrid} />
                                <ImageCard label="Artisan Soaps" field="beautySoapImage" hint="Category thumbnail for Beauty Soaps" icon={FiGrid} />
                                <ImageCard label="All / Best Sellers" field="allBestSellersImage" hint="Category thumbnail for the All category" icon={FiGrid} />
                            </div>
                        </div>
                    )}

                    {/* ── MARKETING ────────────────────────────── */}
                    {activeTab === 'marketing' && (
                        <div className="settings-section">
                            <div className="settings-section-title">Marketing Media</div>
                            <p className="settings-section-desc">Images used in the About page and promotional sections.</p>
                            <div className="settings-image-grid">
                                <ImageCard label="Our Story Image" field="ourStoryImage" hint="Photo shown in the About / Our Story section" icon={FiCamera} />
                                <ImageCard label="Side Ad" field="sideAd" hint="Sidebar promotional banner" icon={FiCamera} />
                            </div>
                        </div>
                    )}

                    {/* ── PAYMENTS ─────────────────────────────── */}
                    {activeTab === 'payments' && (
                        <div className="settings-section">
                            <div className="settings-section-title">GCash Account Details</div>
                            <p className="settings-section-desc">Manage your GCash receiving account details displayed on the checkout page.</p>

                            <div className="settings-form-row" style={{ marginBottom: '1.5rem' }}>
                                <div className="settings-form-group">
                                    <label>GCash Account Name</label>
                                    <input
                                        className="settings-input"
                                        value={settings.gcashAccountName || ''}
                                        onChange={e => setSettings(p => ({ ...p, gcashAccountName: e.target.value }))}
                                        placeholder="e.g. AmaraCé Skincare / Maria S."
                                    />
                                    <small style={{ color: '#888', marginTop: '4px', fontSize: '0.8rem' }}>Customer sees this name to confirm they're paying the right account</small>
                                </div>
                                <div className="settings-form-group">
                                    <label>GCash Mobile Number</label>
                                    <input
                                        className="settings-input"
                                        value={settings.gcashAccountNumber || ''}
                                        onChange={e => setSettings(p => ({ ...p, gcashAccountNumber: e.target.value }))}
                                        placeholder="e.g. 0917 123 4567"
                                    />
                                    <small style={{ color: '#888', marginTop: '4px', fontSize: '0.8rem' }}>Supports one-click copy on the checkout page</small>
                                </div>
                            </div>

                            <div className="settings-section-title" style={{ marginTop: '2rem' }}>GCash QR Code</div>
                            <p className="settings-section-desc">Upload your high-resolution GCash QR code image scanned by customers during checkout.</p>
                            <div className="settings-image-grid">
                                <ImageCard label="GCash QR Code" field="gcashQRCode" hint="Square or standard QR code screenshot" icon={FiCreditCard} />
                            </div>

                            <div className="settings-section-title" style={{ marginTop: '2rem' }}>Payment Method Icons</div>
                            <p className="settings-section-desc">Icons shown in the Cart and Checkout "We Accept" section (max 3). Upload your GCash logo, COD icon, etc.</p>
                            <ArrayImageSection label="Payment Icons" field="paymentLogos" maxCount={3} hint="Square icons recommended. PNG with transparent background." icon={FiCreditCard} />
                        </div>
                    )}

                    {/* ── TEAM & GALLERY ───────────────────────── */}
                    {activeTab === 'team' && (
                        <div className="settings-section">
                            <div className="settings-section-title">Team Photos</div>
                            <p className="settings-section-desc">Photos of your team shown on the About page (max 3).</p>
                            <ArrayImageSection label="Team Photos" field="teamImages" maxCount={3} hint="Recommended: square photos, 800×800px." icon={FiUsers} />

                            <div className="settings-section-title" style={{ marginTop: '2rem' }}>Instagram / Gallery</div>
                            <p className="settings-section-desc">Up to 6 images shown in the "Follow Our Journey" gallery section.</p>
                            <ArrayImageSection label="Gallery Images" field="galleryImages" maxCount={6} hint="Square images recommended, 600×600px." icon={FiCamera} />
                        </div>
                    )}

                </div>

            </main>
        </div>
    );
};

export default AdminSettings;
